(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const revealObserver = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        revealObserver.unobserve(entry.target);
      }
    });
  }, { threshold: .08 }) : null;
  function reveal(root = document) {
    if (!revealObserver || reducedMotion.matches) return;
    document.documentElement.classList.add('yuumae-reveal-ready');
    root.querySelectorAll('[data-reveal]').forEach(el => revealObserver.observe(el));
  }
  reveal();
  document.addEventListener('shopify:section:load', event => reveal(event.target));
  document.addEventListener('shopify:block:select', event => {
    event.target.closest('[data-reveal]')?.classList.add('is-visible');
    event.target.closest('details')?.setAttribute('open', '');
    event.target.querySelector('[data-accordion-trigger][aria-expanded="false"]')?.click();
  });

  class YuumaeHeader extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.menu = this.querySelector('[data-mobile-menu]');
      this.update = () => this.classList.toggle('is-scrolled', window.scrollY > 24);
      this.update();
      window.addEventListener('scroll', this.update, { ...options, passive: true });
      const disclosures = [...this.querySelectorAll('[data-nav-disclosure]')];
      const closeAll = except => disclosures.forEach(menu => { if (menu !== except) menu.open = false; });
      disclosures.forEach(menu => {
        menu.addEventListener('toggle', () => { if (menu.open) closeAll(menu); }, options);
        menu.addEventListener('pointerenter', event => {
          if (event.pointerType === 'mouse' && window.innerWidth >= 1024) {
            clearTimeout(this.closeTimer); closeAll(menu); menu.open = true;
          }
        }, options);
      });
      this.addEventListener('pointerenter', () => clearTimeout(this.closeTimer), options);
      this.addEventListener('pointerleave', event => {
        if (event.pointerType === 'mouse' && window.innerWidth >= 1024) this.closeTimer = setTimeout(() => closeAll(), 180);
      }, options);
      this.addEventListener('keydown', event => {
        if (event.key === 'Escape') {
          const openMenu = event.target.closest('details[open]');
          if (openMenu) { event.preventDefault(); openMenu.open = false; openMenu.querySelector('summary').focus(); }
          else closeAll();
        }
      }, options);
      this.addEventListener('focusout', () => {
        requestAnimationFrame(() => {
          disclosures.forEach(menu => { if (!menu.contains(document.activeElement)) menu.open = false; });
          if (!this.contains(document.activeElement) && this.menu) this.menu.open = false;
        });
      }, options);
      this.addEventListener('click', event => {
        if (event.target.closest('a')) { closeAll(); if (this.menu) this.menu.open = false; }
      }, options);
      document.addEventListener('click', event => {
        if (!this.contains(event.target)) { closeAll(); if (this.menu) this.menu.open = false; }
      }, options);
      const mobileViewport = window.matchMedia('(max-width: 1023px)');
      mobileViewport.addEventListener('change', () => { closeAll(); if (this.menu) this.menu.open = false; }, options);
    }

    disconnectedCallback() { this.abort?.abort(); clearTimeout(this.closeTimer); }
  }

  class YuumaeRail extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.rail = this.querySelector('[data-rail]');
      this.controls = this.querySelector('[data-rail-controls]');
      if (!this.rail || !this.controls) return;
      this.previous = this.querySelector('[data-rail-previous]');
      this.next = this.querySelector('[data-rail-next]');
      const update = () => {
        const max = this.rail.scrollWidth - this.rail.clientWidth;
        this.controls.hidden = max < 4;
        this.previous.disabled = this.rail.scrollLeft < 4;
        this.next.disabled = this.rail.scrollLeft > max - 4;
      };
      const move = direction => this.rail.scrollBy({ left: direction * (this.rail.firstElementChild.getBoundingClientRect().width + parseFloat(getComputedStyle(this.rail).gap)), behavior: reducedMotion.matches ? 'instant' : 'smooth' });
      this.previous.addEventListener('click', () => move(-1), options);
      this.next.addEventListener('click', () => move(1), options);
      this.rail.addEventListener('scroll', update, { ...options, passive: true });
      this.resize = new ResizeObserver(update); this.resize.observe(this.rail); update();
    }
    disconnectedCallback() { this.abort?.abort(); this.resize?.disconnect(); }
  }

  class YuumaeAccordion extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      this.querySelectorAll('[data-accordion-item]').forEach(item => this.togglePanel(item, item.hasAttribute('data-open'), false));
      this.addEventListener('click', event => {
        const trigger = event.target.closest('[data-accordion-trigger]');
        if (trigger) {
          const item = trigger.closest('[data-accordion-item]');
          const open = trigger.getAttribute('aria-expanded') !== 'true';
          if (open) this.querySelectorAll('[data-accordion-item]').forEach(other => { if (other !== item) this.togglePanel(other, false); });
          this.togglePanel(item, open); return;
        }
        const summary = event.target.closest('summary');
        if (!summary || !this.contains(summary)) return;
        event.preventDefault();
        const detail = summary.parentElement;
        const shouldOpen = detail.dataset.animating === 'closing' || !detail.open;
        if (shouldOpen) this.querySelectorAll('details[open]').forEach(other => { if (other !== detail) this.animate(other, false); });
        this.animate(detail, shouldOpen);
      }, { signal: this.abort.signal });
    }
    togglePanel(item, open, animate = true) {
      const button = item.querySelector('[data-accordion-trigger]');
      const panel = item.querySelector('[data-accordion-panel]');
      const start = panel.hidden ? 0 : panel.getBoundingClientRect().height;
      panel.getAnimations().forEach(animation => animation.cancel());
      button.setAttribute('aria-expanded', String(open));
      panel.hidden = false; panel.inert = !open;
      if (!animate || reducedMotion.matches) { panel.hidden = !open; panel.style.overflow = ''; return; }
      const end = open ? panel.scrollHeight : 0;
      const style = getComputedStyle(panel);
      const paddingTop = style.paddingTop, paddingBottom = style.paddingBottom;
      panel.style.overflow = 'hidden';
      const animation = panel.animate({height:[`${start}px`,`${end}px`],opacity:[open ? 0 : 1,open ? 1 : 0],paddingTop:[open ? '0px' : paddingTop,open ? paddingTop : '0px'],paddingBottom:[open ? '0px' : paddingBottom,open ? paddingBottom : '0px']}, {duration:240,easing:'cubic-bezier(.2,.8,.2,1)'});
      animation.onfinish = () => { panel.hidden = !open; panel.style.overflow = ''; };
    }
    animate(detail, open) {
      const start = detail.getBoundingClientRect().height;
      detail.getAnimations().forEach(animation => animation.cancel());
      detail.open = true;
      const end = open ? detail.scrollHeight : detail.querySelector('summary').getBoundingClientRect().height;
      if (reducedMotion.matches) { detail.open = open; return; }
      detail.dataset.animating = open ? 'opening' : 'closing';
      const animation = detail.animate({ height: [`${start}px`, `${end}px`] }, { duration: 240, easing: 'cubic-bezier(.2,.8,.2,1)' });
      animation.onfinish = () => { detail.open = open; delete detail.dataset.animating; };
    }
    disconnectedCallback() { this.abort?.abort(); }
  }

  class YuumaeProductForm extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      this.form = this.querySelector('form');
      this.form?.addEventListener('submit', event => this.submit(event), { signal: this.abort.signal });
    }
    async submit(event) {
      event.preventDefault();
      if (this.busy || !this.form.reportValidity()) return;
      const buttons = [...this.form.elements].filter(element => element.type === 'submit');
      if (buttons.every(button => button.disabled)) return;
      this.busy = true;
      const data = new FormData(this.form);
      const originals = buttons.map(button => ({button, label:button.querySelector('[data-button-label]') || button, text:(button.querySelector('[data-button-label]') || button).textContent, disabled:button.disabled}));
      const controls = [...this.form.elements].filter(element => ['INPUT','SELECT','BUTTON'].includes(element.tagName) && element.type !== 'submit');
      const disabledStates = controls.map(control => control.disabled);
      const error = this.querySelector('[data-product-error]');
      const cartDrawer = this.closest('yuumae-cart-drawer');
      if (error) error.hidden = true;
      controls.forEach(control => { control.disabled = true; });
      originals.forEach(({button,label}) => {button.disabled = true;button.setAttribute('aria-busy','true');label.textContent = this.dataset.adding;});
      this.dispatchEvent(new CustomEvent('yuumae:product-submitting',{bubbles:true,detail:{busy:true}}));
      const root = window.Shopify?.routes?.root || '/';
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(),20000);
      try {
        let result;
        if (window.YuumaeCart) result = await window.YuumaeCart.add(data);
        else {
          const response = await fetch(`${root}cart/add.js`, {method:'POST',body:data,headers:{Accept:'application/json'},signal:controller.signal});
          result = await response.json();
          if (!response.ok) throw new Error(result.description || this.dataset.error);
        }
        originals.forEach(({label}) => {label.textContent = this.dataset.added;});
        const status = document.querySelector('[data-cart-status]');
        if (status) { status.textContent = this.dataset.added;status.hidden = false;clearTimeout(window.yuumaeToastTimer);window.yuumaeToastTimer = setTimeout(() => {status.hidden = true;},4500); }
        document.dispatchEvent(new CustomEvent('yuumae:cart-added',{detail:{...result,trigger:event.submitter || buttons[0]}}));
        if (!window.YuumaeCart) try {
          const cartResponse = await fetch(`${root}cart.js`,{headers:{Accept:'application/json'},signal:controller.signal});
          if (cartResponse.ok) {
            const cart = await cartResponse.json();
            document.querySelectorAll('[data-cart-count]').forEach(el => {el.textContent = cart.item_count;});
            document.dispatchEvent(new CustomEvent('yuumae:cart-update',{detail:cart}));
          }
        } catch (_) { /* The add succeeded; do not invite a duplicate add on a count refresh failure. */ }
        await new Promise(resolve => setTimeout(resolve,1000));
      } catch (failure) {
        const errorMessage = failure.name === 'Error' ? failure.message : this.dataset.error;
        if (cartDrawer?.error) cartDrawer.error(errorMessage);
        else if (error) { error.textContent = errorMessage;error.hidden = false; }
        const status = document.querySelector('[data-cart-status]');
        if (status && this.closest('yuumae-product')?.querySelector('[data-sticky-add]:not([hidden])')) {status.textContent = error?.textContent || this.dataset.error;status.hidden = false;clearTimeout(window.yuumaeToastTimer);window.yuumaeToastTimer = setTimeout(() => {status.hidden = true;},6000);}
      } finally {
        clearTimeout(timeout);
        originals.forEach(({button,label,text,disabled}) => {button.disabled = disabled;button.removeAttribute('aria-busy');label.textContent = text;});
        controls.forEach((control,index) => {control.disabled = disabledStates[index];});
        this.busy = false;
        this.dispatchEvent(new CustomEvent('yuumae:product-submitting',{bubbles:true,detail:{busy:false}}));
      }
    }
    disconnectedCallback() { this.abort?.abort(); }
  }
  document.addEventListener('click', event => {
    const link = event.target.closest('[data-accordion-link]');
    if (!link) return;
    const panel = document.getElementById(link.hash.slice(1));
    const accordion = panel?.closest('yuumae-accordion');
    if (!accordion) return;
    event.preventDefault();
    accordion.querySelectorAll('[data-accordion-item]').forEach(item => accordion.togglePanel(item, item.contains(panel), false));
    const button = panel.previousElementSibling.querySelector('button');
    button.focus({preventScroll:true});button.scrollIntoView({behavior:reducedMotion.matches ? 'instant' : 'smooth',block:'start'});
  });
  customElements.define('yuumae-header', YuumaeHeader);
  customElements.define('yuumae-rail', YuumaeRail);
  customElements.define('yuumae-accordion', YuumaeAccordion);
  customElements.define('yuumae-product-form', YuumaeProductForm);
})();
